// src/pages/PublicSafetySentry.jsx
// Public Safety Sentry - AI-Powered Law Enforcement Operations
// Edge AI Platform for Officer Safety & Resource Optimization
// MWC Barcelona Demo - Celerway Stratus

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ════════════════════════════════════════════════════════════════════════════
// MOCK PUBLIC SAFETY DATA - Simulates police/emergency services operations
// ════════════════════════════════════════════════════════════════════════════

const MOCK_PRECINCT_GATEWAY = {
  id: 'gateway-precinct-central',
  name: 'Central Precinct Hub',
  location: 'Manchester Central Station',
  status: 'Online',
  unitCount: 10,
  activeIncidents: 4,
  stratusModel: 'Stratus 5G Tactical',
  connectivity: '5G + FirstNet',
  lastSync: new Date().toISOString(),
  coverage: '94%',  // Radio coverage percentage
};

const MOCK_UNITS = [
  // Patrol Vehicles
  {
    id: 'UNIT-42',
    type: 'Patrol Vehicle',
    category: 'patrol',
    callsign: 'Charlie-42',
    officer: 'Sgt. James Walker',
    badgeNumber: 'PC-4421',
    status: 'traffic-stop',
    location: { lat: 53.4808, lng: -2.2426, address: 'A56 Chester Road' },
    fuel: 67,
    lastCheckIn: '2025-01-31T14:42:00Z',  // 18 mins ago - trigger alert
    bodycamStatus: 'recording',
    mdtStatus: 'online',
    radioSignal: 92,
    currentCall: 'Traffic stop - suspicious vehicle',
    priority: 'routine',
  },
  {
    id: 'UNIT-17',
    type: 'Patrol Vehicle',
    category: 'patrol',
    callsign: 'Charlie-17',
    officer: 'PC Sarah Mitchell',
    badgeNumber: 'PC-3892',
    status: 'en-route',
    location: { lat: 53.4631, lng: -2.2914, address: 'Stretford Road' },
    fuel: 12,  // LOW - trigger alert
    lastCheckIn: '2025-01-31T14:58:00Z',
    bodycamStatus: 'recording',
    mdtStatus: 'online',
    radioSignal: 88,
    currentCall: 'Domestic disturbance - priority',
    priority: 'immediate',
  },
  {
    id: 'UNIT-23',
    type: 'Patrol Vehicle',
    category: 'patrol',
    callsign: 'Charlie-23',
    officer: 'PC David Chen',
    badgeNumber: 'PC-5567',
    status: 'on-scene',
    location: { lat: 53.4543, lng: -2.2567, address: 'Moss Side District' },
    fuel: 78,
    lastCheckIn: '2025-01-31T14:56:00Z',
    bodycamStatus: 'offline',  // ALERT - bodycam offline during call
    mdtStatus: 'online',
    radioSignal: 45,  // LOW signal
    currentCall: 'Burglary in progress',
    priority: 'immediate',
  },
  {
    id: 'UNIT-31',
    type: 'Patrol Vehicle',
    category: 'patrol',
    callsign: 'Charlie-31',
    officer: 'PC Emma Thompson',
    badgeNumber: 'PC-2234',
    status: 'available',
    location: { lat: 53.4774, lng: -2.2309, address: 'Piccadilly Station' },
    fuel: 89,
    lastCheckIn: '2025-01-31T14:59:00Z',
    bodycamStatus: 'standby',
    mdtStatus: 'online',
    radioSignal: 98,
    currentCall: null,
    priority: null,
  },
  {
    id: 'UNIT-56',
    type: 'Patrol Vehicle',
    category: 'patrol',
    callsign: 'Charlie-56',
    officer: 'PC Robert Davis',
    badgeNumber: 'PC-6712',
    status: 'meal-break',
    location: { lat: 53.4698, lng: -2.2469, address: 'Oxford Road' },
    fuel: 54,
    lastCheckIn: '2025-01-31T14:45:00Z',
    bodycamStatus: 'standby',
    mdtStatus: 'online',
    radioSignal: 95,
    currentCall: null,
    priority: null,
  },
  // Tactical/Support Units
  {
    id: 'TAC-1',
    type: 'Armed Response',
    category: 'tactical',
    callsign: 'Trojan-1',
    officer: 'Sgt. Mark Stevens (Team Lead)',
    badgeNumber: 'AFO-112',
    status: 'standby',
    location: { lat: 53.4821, lng: -2.2371, address: 'Central Station' },
    fuel: 95,
    lastCheckIn: '2025-01-31T14:58:00Z',
    bodycamStatus: 'standby',
    mdtStatus: 'online',
    radioSignal: 99,
    currentCall: null,
    priority: null,
    teamSize: 4,
  },
  {
    id: 'K9-2',
    type: 'K9 Unit',
    category: 'support',
    callsign: 'Dog-2',
    officer: 'PC Alan Wright & K9 Rex',
    badgeNumber: 'PC-8834',
    status: 'available',
    location: { lat: 53.4589, lng: -2.2712, address: 'Hulme District' },
    fuel: 71,
    lastCheckIn: '2025-01-31T14:57:00Z',
    bodycamStatus: 'standby',
    mdtStatus: 'online',
    radioSignal: 87,
    currentCall: null,
    priority: null,
  },
  // Traffic/Camera Units
  {
    id: 'CAM-A41-N',
    type: 'ANPR Camera',
    category: 'camera',
    callsign: null,
    location: { lat: 53.4912, lng: -2.2156, address: 'A41 Northbound' },
    status: 'alert',  // ANPR hit
    lastCapture: '2025-01-31T14:59:30Z',
    alertType: 'stolen-vehicle',
    plateNumber: 'AB12 CDE',
    vehicleDescription: 'Black BMW 3 Series',
    direction: 'Northbound',
  },
  {
    id: 'CAM-M60-J4',
    type: 'Traffic Camera',
    category: 'camera',
    callsign: null,
    location: { lat: 53.4456, lng: -2.3012, address: 'M60 Junction 4' },
    status: 'operational',
    lastCapture: '2025-01-31T14:59:55Z',
    coverage: 'M60 Eastbound/Westbound',
  },
  // Medical/Fire Support
  {
    id: 'AMBO-7',
    type: 'Ambulance',
    category: 'medical',
    callsign: 'Medic-7',
    crew: 'Paramedic Team Alpha',
    status: 'en-route',
    location: { lat: 53.4567, lng: -2.2634, address: 'Princess Road' },
    lastUpdate: '2025-01-31T14:58:00Z',
    destination: 'Moss Side - Burglary (injury reported)',
    eta: '4 mins',
  },
];

// ════════════════════════════════════════════════════════════════════════════
// AI STATUS TYPES - Public Safety specific
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS = {
  CRITICAL: { icon: '🚨', text: 'CRITICAL', bg: 'bg-red-500', glow: 'from-red-500/50 to-rose-500/50', color: 'text-red-400' },
  ALERT: { icon: '⚠️', text: 'ALERT', bg: 'bg-amber-500', glow: 'from-amber-500/50 to-yellow-500/50', color: 'text-amber-400' },
  ACTIVE: { icon: '🔵', text: 'ACTIVE', bg: 'bg-blue-500', glow: 'from-blue-500/50 to-cyan-500/50', color: 'text-blue-400', pulse: true },
  AVAILABLE: { icon: '✓', text: 'AVAILABLE', bg: 'bg-emerald-500', glow: 'from-emerald-500/30 to-teal-500/30', color: 'text-emerald-400' },
  OFFLINE: { icon: '○', text: 'OFFLINE', bg: 'bg-slate-500', glow: 'from-slate-500/30 to-gray-500/30', color: 'text-slate-400' },
};

const UNIT_STATUS_COLORS = {
  'traffic-stop': { bg: 'bg-amber-500', text: 'text-amber-300', label: 'Traffic Stop' },
  'on-scene': { bg: 'bg-red-500', text: 'text-red-300', label: 'On Scene' },
  'en-route': { bg: 'bg-blue-500', text: 'text-blue-300', label: 'En Route' },
  'available': { bg: 'bg-emerald-500', text: 'text-emerald-300', label: 'Available' },
  'meal-break': { bg: 'bg-slate-500', text: 'text-slate-300', label: 'Meal Break' },
  'standby': { bg: 'bg-cyan-500', text: 'text-cyan-300', label: 'Standby' },
  'operational': { bg: 'bg-emerald-500', text: 'text-emerald-300', label: 'Operational' },
  'alert': { bg: 'bg-red-500', text: 'text-red-300', label: 'ALERT' },
};

const CATEGORY_STYLES = {
  patrol: { icon: '🚔', color: 'text-blue-300', bg: 'from-blue-400/30 to-cyan-500/30', border: 'border-blue-400/60' },
  tactical: { icon: '🛡️', color: 'text-red-300', bg: 'from-red-400/30 to-orange-500/30', border: 'border-red-400/60' },
  support: { icon: '🐕', color: 'text-amber-300', bg: 'from-amber-400/30 to-yellow-500/30', border: 'border-amber-400/60' },
  camera: { icon: '📹', color: 'text-purple-300', bg: 'from-purple-400/30 to-violet-500/30', border: 'border-purple-400/60' },
  medical: { icon: '🚑', color: 'text-emerald-300', bg: 'from-emerald-400/30 to-teal-500/30', border: 'border-emerald-400/60' },
};

// ════════════════════════════════════════════════════════════════════════════
// AI ANOMALY DETECTION - Officer Safety & Operational Efficiency
// ════════════════════════════════════════════════════════════════════════════

const detectPublicSafetyAnomalies = (units) => {
  const anomalies = [];
  const now = Date.now();
  
  units.forEach(unit => {
    // Skip cameras for most checks
    if (unit.category === 'camera') {
      // ANPR Alert
      if (unit.alertType === 'stolen-vehicle') {
        anomalies.push({
          id: `anpr-${unit.id}`,
          type: 'ANPR_HIT',
          unitId: unit.id,
          severity: 'critical',
          metric: unit.plateNumber,
          description: `Stolen vehicle detected: ${unit.vehicleDescription}`,
          location: unit.location.address,
          recommendation: 'Dispatch nearest available unit for intercept. Vehicle heading northbound.',
          confidence: 98,
          action: {
            type: 'AUTO_DISPATCH',
            target: 'UNIT-31',
            description: 'Dispatch Charlie-31 for intercept',
          },
        });
      }
      return;
    }
    
    if (unit.category === 'medical') return;
    
    // Officer welfare - no check-in
    if (unit.lastCheckIn) {
      const lastCheckIn = new Date(unit.lastCheckIn).getTime();
      const minutesSince = (now - lastCheckIn) / 60000;
      
      if (minutesSince > 15 && (unit.status === 'traffic-stop' || unit.status === 'on-scene')) {
        anomalies.push({
          id: `welfare-${unit.id}`,
          type: 'WELFARE_CHECK',
          unitId: unit.id,
          severity: 'critical',
          metric: `${Math.floor(minutesSince)} min`,
          description: `No check-in for ${Math.floor(minutesSince)} minutes during ${unit.status.replace('-', ' ')}`,
          officer: unit.officer,
          recommendation: 'Initiate welfare check. Attempt radio contact. Dispatch backup to last known location.',
          confidence: 94,
          action: {
            type: 'WELFARE_CHECK',
            description: 'Initiate officer welfare protocol',
          },
        });
      }
    }
    
    // Bodycam offline during active call
    if (unit.bodycamStatus === 'offline' && (unit.status === 'on-scene' || unit.status === 'traffic-stop')) {
      anomalies.push({
        id: `bodycam-${unit.id}`,
        type: 'BODYCAM_OFFLINE',
        unitId: unit.id,
        severity: 'critical',
        metric: 'OFFLINE',
        description: `Bodycam offline during active call`,
        officer: unit.officer,
        recommendation: 'Alert supervisor immediately. Require officer to activate bodycam or provide justification.',
        confidence: 99,
        action: {
          type: 'SUPERVISOR_ALERT',
          description: 'Notify duty supervisor',
        },
      });
    }
    
    // Low fuel on priority call
    if (unit.fuel < 20 && unit.priority === 'immediate') {
      anomalies.push({
        id: `fuel-${unit.id}`,
        type: 'FUEL_CRITICAL',
        unitId: unit.id,
        severity: 'warning',
        metric: `${unit.fuel}%`,
        description: `Low fuel while assigned to priority call`,
        officer: unit.officer,
        recommendation: 'Dispatch backup unit to assume call. Route original unit to nearest fuel station.',
        confidence: 96,
        action: {
          type: 'DISPATCH_BACKUP',
          target: 'UNIT-31',
          description: 'Dispatch backup and reassign',
        },
      });
    }
    
    // Low radio signal
    if (unit.radioSignal && unit.radioSignal < 50) {
      anomalies.push({
        id: `radio-${unit.id}`,
        type: 'RADIO_DEADZONE',
        unitId: unit.id,
        severity: 'warning',
        metric: `${unit.radioSignal}%`,
        description: `Unit in low radio coverage area`,
        officer: unit.officer,
        recommendation: 'Monitor closely. Consider repositioning relay unit to improve coverage.',
        confidence: 87,
      });
    }
  });
  
  return anomalies.sort((a, b) => {
    const sevOrder = { critical: 0, warning: 1, info: 2 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

const getUnitAIStatus = (unit, anomalies) => {
  const unitAnomalies = anomalies.filter(a => a.unitId === unit.id);
  
  if (unit.status === 'offline') return AI_STATUS.OFFLINE;
  
  const hasCritical = unitAnomalies.some(a => a.severity === 'critical');
  const hasWarning = unitAnomalies.some(a => a.severity === 'warning');
  
  if (hasCritical) return AI_STATUS.CRITICAL;
  if (hasWarning) return AI_STATUS.ALERT;
  if (unit.status === 'en-route' || unit.status === 'on-scene' || unit.status === 'traffic-stop') return AI_STATUS.ACTIVE;
  return AI_STATUS.AVAILABLE;
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

// Command Center Node
const CommandCenterNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-400 !border-2 !border-white" />
    
    <div className="absolute -inset-2 bg-gradient-to-r from-blue-400/30 via-cyan-400/30 to-blue-400/30 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
    
    <div className="relative bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-blue-400/50 rounded-2xl p-6 min-w-[240px] shadow-2xl shadow-blue-500/20">
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400 shadow-lg shadow-emerald-400/50" />
      </div>
      
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-blue-400/30 to-cyan-500/30 rounded-xl flex items-center justify-center border border-blue-400/50">
          <svg className="w-8 h-8 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
      </div>
      
      <div className="text-center">
        <div className="text-sm font-semibold text-white tracking-wide">{data.label}</div>
        <div className="text-xs text-slate-300 mt-1">{data.sublabel}</div>
      </div>
      
      <div className="mt-4 pt-3 border-t border-slate-600/50 grid grid-cols-3 gap-2">
        <div className="text-center">
          <div className="text-lg font-bold text-blue-300">{data.unitCount || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Units</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-amber-300">{data.activeIncidents || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Active</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-300">{data.coverage || '0%'}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Coverage</div>
        </div>
      </div>
      
      <div className="mt-3 flex items-center justify-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
        <span className="text-[10px] text-blue-300 font-medium">AI Sentry Command Active</span>
      </div>
    </div>
  </div>
);

// Precinct Gateway Node
const PrecinctGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      <div className={`absolute -inset-1.5 bg-gradient-to-r ${isOnline ? 'from-emerald-400/30 to-teal-400/30' : 'from-red-400/30 to-orange-400/30'} rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-300`} />
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${isOnline ? 'border-emerald-400/60' : 'border-red-400/60'} rounded-xl p-4 min-w-[260px] shadow-xl transition-all duration-300 group-hover:scale-105`}>
        <div className="absolute -top-1 -right-1 w-3 h-3">
          {isOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-emerald-400' : 'bg-red-400'} shadow-lg`} />
        </div>
        
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnline ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
            <svg className={`w-5 h-5 ${isOnline ? 'text-emerald-300' : 'text-red-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{data.name}</div>
            <div className="text-[10px] text-slate-400 truncate">{data.location}</div>
          </div>
        </div>
        
        <div className="mt-3 pt-2 border-t border-slate-500/30 grid grid-cols-3 gap-2 text-xs">
          <div>
            <span className="text-slate-400">Status</span>
            <div className={`font-semibold ${isOnline ? 'text-emerald-300' : 'text-red-300'}`}>{data.status}</div>
          </div>
          <div className="text-center">
            <span className="text-slate-400">Units</span>
            <div className="font-semibold text-blue-300">{data.unitCount}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-400">Active</span>
            <div className="font-semibold text-amber-300">{data.activeIncidents}</div>
          </div>
        </div>
        
        <div className="mt-2 text-center">
          <span className="text-[9px] px-2 py-0.5 bg-slate-800/50 rounded-full text-cyan-300 font-mono">
            {data.stratusModel} • {data.connectivity}
          </span>
        </div>
      </div>
    </div>
  );
};

// Field Unit Node (Patrol, Tactical, Support)
const FieldUnitNode = ({ data }) => {
  const categoryStyle = CATEGORY_STYLES[data.category] || CATEGORY_STYLES.patrol;
  const statusInfo = UNIT_STATUS_COLORS[data.status] || UNIT_STATUS_COLORS['available'];
  const aiStatus = data.aiStatus || AI_STATUS.AVAILABLE;
  const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.ALERT;

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      {hasAlert && (
        <div className={`absolute -inset-2 bg-gradient-to-r ${aiStatus.glow} rounded-xl blur-lg opacity-60 ${aiStatus.pulse ? 'animate-pulse' : ''}`} />
      )}
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${hasAlert ? (aiStatus === AI_STATUS.CRITICAL ? 'border-red-400' : 'border-amber-400') : categoryStyle.border} rounded-lg p-3 min-w-[180px] shadow-lg transition-all duration-300 group-hover:scale-105`}>
        
        {/* AI Status Badge */}
        <div className={`absolute -top-2 -right-2 px-2 py-0.5 ${aiStatus.bg} text-white text-[9px] font-bold rounded-full shadow-lg flex items-center gap-1 ${aiStatus.pulse ? 'animate-pulse' : ''}`}>
          <span>{aiStatus.icon}</span>
          <span>{aiStatus.text}</span>
        </div>
        
        {/* Unit header */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br ${categoryStyle.bg}`}>
            <span className="text-xl">{categoryStyle.icon}</span>
          </div>
          <div>
            <div className="text-sm font-bold text-white">{data.callsign || data.id}</div>
            <div className="text-[10px] text-slate-400">{data.type}</div>
          </div>
        </div>
        
        {/* Officer */}
        {data.officer && (
          <div className="text-xs text-slate-300 mb-2 truncate">
            <span className="text-slate-500">Officer:</span> {data.officer}
          </div>
        )}
        
        {/* Status badge */}
        <div className="mb-2">
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusInfo.bg}/30 ${statusInfo.text} border border-current/30`}>
            {statusInfo.label}
          </span>
          {data.priority === 'immediate' && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/30 text-red-300 border border-red-400/30 ml-1">
              PRIORITY
            </span>
          )}
        </div>
        
        {/* Current call if active */}
        {data.currentCall && (
          <div className="bg-slate-800/60 rounded px-2 py-1.5 mb-2">
            <div className="text-[10px] text-slate-500 uppercase">Current Call</div>
            <div className="text-xs text-cyan-300 truncate">{data.currentCall}</div>
          </div>
        )}
        
        {/* Key metrics */}
        {data.category !== 'camera' && data.category !== 'medical' && (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className={`rounded px-1 py-0.5 ${data.fuel < 20 ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${data.fuel < 20 ? 'text-red-300' : 'text-slate-300'}`}>{data.fuel}%</div>
              <div className="text-[8px] text-slate-500">FUEL</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${data.bodycamStatus === 'offline' ? 'bg-red-500/20' : data.bodycamStatus === 'recording' ? 'bg-emerald-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${data.bodycamStatus === 'offline' ? 'text-red-300' : data.bodycamStatus === 'recording' ? 'text-emerald-300' : 'text-slate-300'}`}>
                {data.bodycamStatus === 'recording' ? 'REC' : data.bodycamStatus === 'offline' ? 'OFF' : 'STBY'}
              </div>
              <div className="text-[8px] text-slate-500">CAM</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${data.radioSignal < 50 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${data.radioSignal < 50 ? 'text-amber-300' : 'text-slate-300'}`}>{data.radioSignal}%</div>
              <div className="text-[8px] text-slate-500">RADIO</div>
            </div>
          </div>
        )}
        
        {/* Camera-specific display */}
        {data.category === 'camera' && data.alertType && (
          <div className="bg-red-500/20 border border-red-400/50 rounded p-2">
            <div className="text-[10px] text-red-300 font-bold">🚨 ANPR ALERT</div>
            <div className="text-xs text-white font-mono">{data.plateNumber}</div>
            <div className="text-[10px] text-slate-300">{data.vehicleDescription}</div>
            <div className="text-[10px] text-slate-400">{data.direction}</div>
          </div>
        )}
        
        {/* Medical unit display */}
        {data.category === 'medical' && (
          <div className="bg-slate-800/60 rounded px-2 py-1.5">
            <div className="text-[10px] text-slate-500">Destination</div>
            <div className="text-xs text-emerald-300 truncate">{data.destination}</div>
            {data.eta && <div className="text-[10px] text-amber-300">ETA: {data.eta}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

// Node types registry
const nodeTypes = {
  commandCenter: CommandCenterNode,
  precinctGateway: PrecinctGatewayNode,
  fieldUnit: FieldUnitNode,
};

// ════════════════════════════════════════════════════════════════════════════
// AI ACTIVITY PANEL
// ════════════════════════════════════════════════════════════════════════════

const AIActivityPanel = ({ isOpen, onClose, anomalies, units }) => {
  const [activeTab, setActiveTab] = useState('alerts');
  const [executingAction, setExecutingAction] = useState(null);
  
  if (!isOpen) return null;
  
  const handleExecuteAction = async (anomaly) => {
    setExecutingAction(anomaly.id);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setExecutingAction(null);
  };
  
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  const patrolUnits = units.filter(u => u.category === 'patrol');
  const availableUnits = patrolUnits.filter(u => u.status === 'available').length;
  
  return (
    <div className="absolute top-0 right-0 h-full w-96 bg-slate-900/98 backdrop-blur-xl border-l border-slate-700/50 shadow-2xl z-50 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <span className="text-lg">🛡️</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">AI Sentry Command</h3>
              <div className="text-[10px] text-slate-400">{anomalies.length} alerts • {units.length} units</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex items-center gap-2 mt-3">
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 rounded text-emerald-300 text-xs">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Monitoring Active
          </div>
          <div className="text-[10px] text-slate-500">CAD Integration Ready</div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-slate-700/50">
        <button
          onClick={() => setActiveTab('alerts')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'alerts'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <span className="flex items-center justify-center gap-1">
            🚨 Alerts
            {criticalCount > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">{criticalCount}</span>
            )}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('dispatch')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'dispatch'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          📍 Dispatch
        </button>
        <button
          onClick={() => setActiveTab('units')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'units'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          🚔 Units ({availableUnits})
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activeTab === 'alerts' && (
          <>
            {anomalies.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <div className="text-3xl mb-2">✓</div>
                <div>All units nominal</div>
              </div>
            ) : (
              anomalies.map(anomaly => {
                const unit = units.find(u => u.id === anomaly.unitId);
                const categoryStyle = CATEGORY_STYLES[unit?.category] || CATEGORY_STYLES.patrol;
                return (
                  <div
                    key={anomaly.id}
                    className={`bg-slate-800/50 rounded-lg border ${
                      anomaly.severity === 'critical' ? 'border-red-500/50' : 'border-amber-500/30'
                    } p-3`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{categoryStyle.icon}</span>
                        <div>
                          <div className="text-sm font-semibold text-white">{unit?.callsign || anomaly.unitId}</div>
                          <div className="text-[10px] text-slate-400">{anomaly.officer || unit?.type}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs font-bold ${anomaly.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                          {anomaly.confidence}%
                        </div>
                        <div className="text-[10px] text-slate-500">confidence</div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-slate-300 mb-2">{anomaly.description}</div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${anomaly.severity === 'critical' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                        {anomaly.metric}
                      </span>
                      <span className="text-[10px] text-slate-500">{anomaly.type.replace(/_/g, ' ')}</span>
                    </div>
                    
                    <div className="bg-slate-700/30 rounded p-2 mb-3">
                      <div className="text-[10px] text-slate-500 uppercase mb-1">AI Recommendation</div>
                      <div className="text-xs text-cyan-300">{anomaly.recommendation}</div>
                    </div>
                    
                    {anomaly.action && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleExecuteAction(anomaly)}
                          disabled={executingAction === anomaly.id}
                          className="flex-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-400 disabled:bg-slate-600 text-white text-xs font-medium rounded transition-colors flex items-center justify-center gap-1"
                        >
                          {executingAction === anomaly.id ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Executing...
                            </>
                          ) : (
                            <>✓ {anomaly.action.type.replace(/_/g, ' ')}</>
                          )}
                        </button>
                        <button className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors">
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}
        
        {activeTab === 'dispatch' && (
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-red-500/20 to-amber-500/20 rounded-lg border border-red-500/30 p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">🚨</span>
                <div>
                  <div className="text-sm font-semibold text-white">Auto-Dispatch Recommendation</div>
                  <div className="text-[10px] text-slate-400">ANPR Alert Response</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-lg font-bold text-emerald-400">98%</div>
                  <div className="text-[10px] text-slate-500">Confidence</div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Incident</div>
                <div className="text-sm text-red-300">Stolen Vehicle Detected</div>
                <div className="text-xs text-white font-mono mt-1">AB12 CDE - Black BMW 3 Series</div>
                <div className="text-xs text-slate-400">A41 Northbound • Heading toward M60</div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Recommended Unit</div>
                <div className="flex items-center gap-2">
                  <span className="text-lg">🚔</span>
                  <div>
                    <div className="text-sm text-white font-semibold">Charlie-31</div>
                    <div className="text-xs text-slate-400">PC Emma Thompson • Available</div>
                  </div>
                </div>
                <div className="text-xs text-emerald-300 mt-2">✓ Nearest available unit • 2.1 km away</div>
              </div>
              
              <div className="flex gap-2">
                <button className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                  🚔 Dispatch Unit
                </button>
                <button className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">
                  Dismiss
                </button>
              </div>
            </div>
            
            <div className="text-center text-[10px] text-slate-500 pt-2">
              Powered by <span className="text-cyan-400">AI Sentry</span>
            </div>
          </div>
        )}
        
        {activeTab === 'units' && (
          <div className="space-y-2">
            {units.filter(u => u.category === 'patrol' || u.category === 'tactical' || u.category === 'support').map(unit => {
              const statusInfo = UNIT_STATUS_COLORS[unit.status];
              const categoryStyle = CATEGORY_STYLES[unit.category];
              return (
                <div key={unit.id} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{categoryStyle.icon}</span>
                      <div>
                        <div className="text-sm font-semibold text-white">{unit.callsign}</div>
                        <div className="text-[10px] text-slate-400">{unit.officer}</div>
                      </div>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${statusInfo.bg}/30 ${statusInfo.text}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  {unit.currentCall && (
                    <div className="text-xs text-slate-300 truncate">{unit.currentCall}</div>
                  )}
                  <div className="flex gap-3 mt-2 text-[10px] text-slate-400">
                    <span>⛽ {unit.fuel}%</span>
                    <span>📹 {unit.bodycamStatus}</span>
                    <span>📡 {unit.radioSignal}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="border-t border-slate-700/50 px-4 py-2 bg-slate-800/50">
        <div className="flex items-center justify-between text-[10px] text-slate-500">
          <span>Last update: Just now</span>
          <button className="text-cyan-400 hover:text-cyan-300">Refresh Now</button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT CARD
// ════════════════════════════════════════════════════════════════════════════

const ProactiveAlert = ({ anomalies, units, onViewDetails }) => {
  const criticalAnomalies = anomalies.filter(a => a.severity === 'critical');
  if (criticalAnomalies.length === 0) return null;
  
  const topAnomaly = criticalAnomalies[0];
  const unit = units.find(u => u.id === topAnomaly.unitId);
  
  return (
    <div className="absolute top-32 left-6 z-40 animate-slide-in-left">
      <div className="bg-gradient-to-r from-red-900/90 to-slate-900/90 backdrop-blur-xl border border-red-500/50 rounded-xl p-4 shadow-2xl shadow-red-500/20 max-w-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-500/30 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xl animate-pulse">🚨</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white mb-1">
              {topAnomaly.type === 'WELFARE_CHECK' ? 'Officer Safety Alert' : 
               topAnomaly.type === 'ANPR_HIT' ? 'ANPR Alert' :
               topAnomaly.type === 'BODYCAM_OFFLINE' ? 'Bodycam Alert' : 'Alert'}
            </div>
            <div className="text-xs text-red-200 mb-2">
              {topAnomaly.description}
            </div>
            <div className="text-[10px] text-slate-400 mb-3">
              {unit?.callsign || topAnomaly.unitId} • {topAnomaly.officer || unit?.officer} • {criticalAnomalies.length > 1 ? `+${criticalAnomalies.length - 1} more` : topAnomaly.type.replace(/_/g, ' ')}
            </div>
            <button 
              onClick={onViewDetails}
              className="px-4 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-medium rounded-lg transition-colors"
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR
// ════════════════════════════════════════════════════════════════════════════

const StatsBar = ({ units, anomalies, coverage, onOpenPanel }) => {
  const patrolUnits = units.filter(u => u.category === 'patrol' || u.category === 'tactical' || u.category === 'support');
  const available = patrolUnits.filter(u => u.status === 'available' || u.status === 'standby').length;
  const onCall = patrolUnits.filter(u => u.status === 'on-scene' || u.status === 'en-route' || u.status === 'traffic-stop').length;
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-emerald-300 font-bold text-lg">{available}</span>
        <span className="text-slate-300 text-xs">Available</span>
      </div>
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-blue-300 font-bold text-lg">{onCall}</span>
        <span className="text-slate-300 text-xs">On Call</span>
      </div>
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-cyan-300 font-bold text-lg">{coverage}</span>
        <span className="text-slate-300 text-xs">Coverage</span>
      </div>
      {criticalCount > 0 && (
        <div className="flex items-center gap-2 bg-red-500/20 rounded-lg px-3 py-2 border border-red-400/50 animate-pulse">
          <span className="text-red-300 font-bold text-lg">{criticalCount}</span>
          <span className="text-red-300/70 text-xs">Alerts</span>
        </div>
      )}
      <button
        onClick={onOpenPanel}
        className="flex items-center gap-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg px-4 py-2 border border-blue-400/50 transition-colors"
      >
        <span className="text-blue-300 text-sm">🛡️</span>
        <span className="text-blue-300 text-xs font-medium">AI Sentry</span>
        {anomalies.length > 0 && (
          <span className="px-1.5 py-0.5 bg-blue-500 text-white text-[10px] rounded-full">{anomalies.length}</span>
        )}
      </button>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND
// ════════════════════════════════════════════════════════════════════════════

const Legend = () => (
  <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-xl p-4 space-y-3">
    <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Legend</div>
    <div className="space-y-2">
      {Object.entries(CATEGORY_STYLES).map(([key, style]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-sm">{style.icon}</span>
          <span className="text-xs text-slate-300 capitalize">{key}</span>
        </div>
      ))}
    </div>
    <div className="pt-2 border-t border-slate-600/50 space-y-2">
      <div className="text-[10px] text-slate-400 uppercase">Unit Status</div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-red-500" />
        <span className="text-xs text-slate-300">On Scene</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-blue-500" />
        <span className="text-xs text-slate-300">En Route</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-emerald-500" />
        <span className="text-xs text-slate-300">Available</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const PublicSafetySentry = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [units] = useState(MOCK_UNITS);
  const [gateway] = useState(MOCK_PRECINCT_GATEWAY);
  const [anomalies, setAnomalies] = useState([]);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Build topology
  const buildTopology = useCallback(() => {
    const detectedAnomalies = detectPublicSafetyAnomalies(units);
    setAnomalies(detectedAnomalies);
    
    const newNodes = [];
    const newEdges = [];
    
    // Layout constants
    const CLOUD_Y = 0;
    const GATEWAY_Y = 180;
    const UNIT_Y_START = 400;
    const CENTER_X = 600;
    const UNIT_GAP = 200;
    
    // Group units by category
    const categories = ['patrol', 'tactical', 'support', 'camera', 'medical'];
    const unitsByCategory = {};
    categories.forEach(cat => {
      unitsByCategory[cat] = units.filter(u => u.category === cat);
    });
    
    // 1. Command Center (Cloud)
    const activeIncidents = units.filter(u => 
      u.status === 'on-scene' || u.status === 'en-route' || u.status === 'traffic-stop'
    ).length;
    
    newNodes.push({
      id: 'command-center',
      type: 'commandCenter',
      position: { x: CENTER_X, y: CLOUD_Y },
      data: {
        label: 'Command Center',
        sublabel: 'Central Dispatch',
        unitCount: units.filter(u => u.category !== 'camera').length,
        activeIncidents,
        coverage: gateway.coverage,
      },
    });
    
    // 2. Precinct Gateway
    newNodes.push({
      id: `gateway-${gateway.id}`,
      type: 'precinctGateway',
      position: { x: CENTER_X, y: GATEWAY_Y },
      data: { ...gateway, activeIncidents },
    });
    
    // Edge: Cloud → Gateway
    newEdges.push({
      id: 'cloud-to-gateway',
      source: 'command-center',
      target: `gateway-${gateway.id}`,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#3b82f6', strokeWidth: 2 },
    });
    
    // 3. Units by category in rows
    let currentY = UNIT_Y_START;
    const CATEGORY_GAP = 180;
    
    categories.forEach(category => {
      const categoryUnits = unitsByCategory[category];
      if (categoryUnits.length === 0) return;
      
      const startX = CENTER_X - ((categoryUnits.length - 1) * UNIT_GAP) / 2;
      
      categoryUnits.forEach((unit, idx) => {
        const aiStatus = getUnitAIStatus(unit, detectedAnomalies);
        const unitId = `unit-${unit.id}`;
        
        newNodes.push({
          id: unitId,
          type: 'fieldUnit',
          position: { x: startX + idx * UNIT_GAP, y: currentY },
          data: { ...unit, aiStatus },
        });
        
        // Edge: Gateway → Unit
        const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.ALERT;
        const categoryStyle = CATEGORY_STYLES[category];
        const edgeColor = hasAlert 
          ? (aiStatus === AI_STATUS.CRITICAL ? '#ef4444' : '#f59e0b')
          : '#3b82f6';
        
        newEdges.push({
          id: `gateway-to-${unitId}`,
          source: `gateway-${gateway.id}`,
          target: unitId,
          type: 'smoothstep',
          animated: true,
          style: { stroke: edgeColor, strokeWidth: 2 },
        });
      });
      
      currentY += CATEGORY_GAP;
    });
    
    return { nodes: newNodes, edges: newEdges };
  }, [units, gateway]);

  // Initialize
  useEffect(() => {
    setLoading(true);
    
    setTimeout(() => {
      const { nodes: newNodes, edges: newEdges } = buildTopology();
      setNodes(newNodes);
      setEdges(newEdges);
      setLoading(false);
    }, 1000);
  }, [buildTopology, setNodes, setEdges]);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-blue-500/30 to-cyan-600/30 rounded-xl flex items-center justify-center border border-blue-400/30">
                <span className="text-xl">🛡️</span>
              </span>
              Public Safety Sentry
            </h1>
            <p className="text-slate-300 text-sm mt-1">AI-Powered Operations • Officer Safety • Resource Optimization</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] px-2 py-1 bg-blue-500/20 text-blue-300 rounded border border-blue-500/30">
                Edge AI Platform Demo
              </span>
              <span className="text-[10px] px-2 py-1 bg-slate-700/50 text-slate-400 rounded">
                MWC Barcelona 2025
              </span>
            </div>
          </div>
          <StatsBar 
            units={units} 
            anomalies={anomalies}
            coverage={gateway.coverage}
            onOpenPanel={() => setIsAIPanelOpen(true)} 
          />
        </div>
      </div>
      
      {/* Proactive Alert */}
      <ProactiveAlert 
        anomalies={anomalies} 
        units={units} 
        onViewDetails={() => setIsAIPanelOpen(true)} 
      />
      
      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-blue-300 text-lg font-medium">AI Sentry Initializing...</div>
            <div className="text-slate-400 text-sm mt-1">Connecting to Command Center</div>
          </div>
        </div>
      )}
      
      {/* React Flow */}
      {!loading && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(59, 130, 246, 0.15)" />
          <Controls className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl" />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'commandCenter') return '#3b82f6';
              if (node.type === 'precinctGateway') return '#34d399';
              if (node.data?.aiStatus === AI_STATUS.CRITICAL) return '#ef4444';
              if (node.data?.aiStatus === AI_STATUS.ALERT) return '#f59e0b';
              if (node.data?.aiStatus === AI_STATUS.ACTIVE) return '#3b82f6';
              return '#60a5fa';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-800/90 !border-slate-600/50 rounded-xl"
          />
          <Panel position="bottom-left"><Legend /></Panel>
        </ReactFlow>
      )}
      
      {/* AI Panel */}
      <AIActivityPanel 
        isOpen={isAIPanelOpen} 
        onClose={() => setIsAIPanelOpen(false)} 
        anomalies={anomalies}
        units={units}
      />
      
      {/* Animations */}
      <style>{`
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default PublicSafetySentry;
